import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import { useAdminAdventPass } from "@/hooks/useAdminAdventPass";
import { Gift, Calendar, TrendingUp } from "lucide-react";

const AdventPassList = () => {
  const { data: purchases, isLoading } = useAdminAdventPass();

  // Calculate stats
  const totalPurchases = purchases?.length || 0;
  const totalCreditsGranted = purchases?.reduce((sum: number, p: any) => sum + (p.credits_granted || 0), 0) || 0;
  const latestPurchase = purchases?.[0]?.redeemed_at 
    ? format(new Date(purchases[0].redeemed_at), "MMM d, yyyy HH:mm")
    : "N/A";

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!purchases || purchases.length === 0) {
    return (
      <div className="space-y-4">
        {/* Stats Cards - Show zeros when no purchases */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardDescription className="flex items-center gap-2 text-sm">
                <Gift className="h-4 w-4" />
                Total Purchases
              </CardDescription>
              <CardTitle className="text-3xl">0</CardTitle>
            </CardHeader>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardDescription className="flex items-center gap-2 text-sm">
                <TrendingUp className="h-4 w-4" />
                Credits Granted
              </CardDescription>
              <CardTitle className="text-3xl">0</CardTitle>
            </CardHeader>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardDescription className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4" />
                Latest Purchase
              </CardDescription>
              <CardTitle className="text-lg">N/A</CardTitle>
            </CardHeader>
          </Card>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle>Advent Pass Purchases</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">No Advent Pass purchases yet.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2 text-sm">
              <Gift className="h-4 w-4" />
              Total Purchases
            </CardDescription>
            <CardTitle className="text-3xl">{totalPurchases}</CardTitle>
          </CardHeader>
        </Card>
        
        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2 text-sm">
              <TrendingUp className="h-4 w-4" />
              Credits Granted
            </CardDescription>
            <CardTitle className="text-3xl">{totalCreditsGranted}</CardTitle>
          </CardHeader>
        </Card>
        
        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4" />
              Latest Purchase
            </CardDescription>
            <CardTitle className="text-lg">{latestPurchase}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Purchases Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Purchases</CardTitle>
        </CardHeader>
        <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left p-2 font-semibold">Name</th>
                <th className="text-left p-2 font-semibold">Email</th>
                <th className="text-left p-2 font-semibold">Phone</th>
                <th className="text-left p-2 font-semibold">Purchase Date</th>
              </tr>
            </thead>
            <tbody>
              {purchases.map((purchase: any) => (
                <tr key={purchase.id} className="border-b hover:bg-muted/50">
                  <td className="p-2">
                    {purchase.profiles ? `${purchase.profiles.first_name || ''} ${purchase.profiles.last_name || ''}`.trim() : "N/A"}
                  </td>
                  <td className="p-2">{purchase.profiles?.email || "N/A"}</td>
                  <td className="p-2">{purchase.profiles?.phone || "N/A"}</td>
                  <td className="p-2">
                    {purchase.redeemed_at
                      ? format(new Date(purchase.redeemed_at), "MMM d, yyyy HH:mm")
                      : "N/A"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
      </Card>
    </div>
  );
};

export default AdventPassList;
